import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export enum Role {
  Admin = 'admin',
  Creator = 'creator',
  RIPTRisk = 'fastrepi',
  Readonly = 'readonly',
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenKey = 'access_token';
  private rolesKey = 'roles';

  private isAuthorized$ = new BehaviorSubject<boolean>(false);
  private _roles$ = new BehaviorSubject<Role[]>(this.getRoles());

  public get isAuthorized() {
    return this.isAuthorized$.asObservable();
  }

  public get roles$() {
    return this._roles$.asObservable();
  }

  public get roles() {
    return this._roles$.value;
  }

  constructor(private http: HttpClient) {}

  setToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setRoles(roles: Role[]) {
    this._roles$.next(roles);
    localStorage.setItem(this.rolesKey, JSON.stringify(roles));
  }

  getRoles(): Role[] {
    const roles = localStorage.getItem(this.rolesKey);
    return roles ? JSON.parse(roles) : [];
  }

  hasRolesCached(): boolean {
    return localStorage.getItem(this.rolesKey) !== null;
  }

  isInRole(role: Role): boolean {
    return this.getRoles().includes(role);
  }

  isReadOnly() {
    const roles = this.getRoles();

    return roles.includes(Role.Readonly) && !roles.includes(Role.Creator) && !roles.includes(Role.Admin)
  }

  isAdmin() {
    return this.getRoles().includes(Role.Admin);
  }

  isCreator() {
    return this.getRoles().includes(Role.Creator);
  }

  clear() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.rolesKey);
  }
}
